// A place card (F3; mockup 07): what it is, tips, the toy it becomes in the Crate, the plans that
// go there, a ☆ for the want list, and "Open in Maps" (a geo: link the phone's own Maps app
// opens; nothing is sent from here). Opened from Map flags, Trip · Today plans and Ideas.
// A link out of the app is for grown-ups only (B3, Play Families): a child never sees "Open in Maps".
import { useMemo } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { Sheet } from '../app/Sheet'
import { useChildSettings } from '../data/controls'
import { record } from '../data/events'
import { useEvents } from '../data/progress'
import { toyFor } from '../domain/crateToys'
import { myPlaces } from '../domain/places'
import { placeSticker } from '../domain/stickers'
import { fmt, t } from '../strings/en'
import { contentLang } from '../audio/tts'

const TOY_EMOJI: Record<string, string> = { ramen: '🍜', bucket: '♨️', chair: '🚡', float: '🫧', can: '🥤', snowman: '⛄', train: '🚃', tower: '🗼' }

export function PlaceSheet({ pack, person, placeId }: { pack: Pack; person: Person; placeId: string }) {
  const place = pack.places.find((p) => p.id === placeId)
  const events = useEvents()
  const mine = useMemo(() => myPlaces(events, person.id), [events, person.id])
  const canWant = useChildSettings(person.kind === 'child' ? person : null)?.canSuggest ?? true
  if (!place) return null
  const wanted = mine.wanted.has(place.id)
  const found = mine.found.has(place.id)
  const plans = pack.itinerary.dayItems.filter((i) => i.placeId === place.id)
  const toy = toyFor(place)
  const sticker = placeSticker(pack, place)
  const geo = `geo:${place.at[0]},${place.at[1]}?q=${place.at[0]},${place.at[1]}(${encodeURIComponent(place.name)})`

  return (
    <Sheet title={place.name}>
      {place.nameJa ? (
        <p className="place-ja" lang={contentLang()}>
          {place.nameJa}
        </p>
      ) : null}
      <div className="panel sheet-panel">
        <div className="row">
          {sticker ? <img className="place-sticker" src={sticker} alt="" width={56} height={56} /> : <span className="fam-av">{TOY_EMOJI[toy] ?? '🎁'}</span>}
          <div>
            <b>{found ? t.place.found : t.place.toy}</b>
            <small>{place.summary}</small>
          </div>
        </div>
        {place.tips.map((tip) => (
          <div className="row" key={tip}>
            <span className="fam-av">💡</span>
            <small>{tip}</small>
          </div>
        ))}
        {plans.map((p) => (
          <div className="row" key={p.id}>
            <span className="fam-av">📅</span>
            <div>
              <b>{p.title}</b>
              <small>{[fmt.day(p.date), p.time].filter(Boolean).join(' · ')}</small>
            </div>
          </div>
        ))}
      </div>
      <div className="two place-actions">
        {canWant ? (
          <button className={`btn ${wanted ? 'y' : 'w'} press`} aria-pressed={wanted} onClick={() => void record(wanted ? 'place.unwanted' : 'place.wanted', { placeId: place.id }, person.id)}>
            {wanted ? t.place.wanted : t.place.want}
          </button>
        ) : null}
        {person.kind !== 'child' ? (
          <a className="btn w press" href={geo}>
            {t.place.maps}
          </a>
        ) : null}
      </div>
    </Sheet>
  )
}
